import {
  RUTERO_VERTICALS,
  VOUCHER_AGENCY_TEXT_KEYS,
  VOUCHER_TEXT_BASE_LANGUAGE,
  VOUCHER_TEXT_LANGUAGES,
  VoucherTextIssue,
  VoucherTextLanguage,
  VoucherTextMode,
  VoucherTextScope,
  normalizeVoucherText,
  voucherConnectorTextKey,
  voucherTextIssues,
  voucherTextMaxLength,
} from '@bandido/shared/types';

const KEY = 'vouchers.config';

/**
 * LA LÓGICA de los textos de la agencia, fuera del componente y sin DOM: qué
 * bloques hay, cuánto entra en cada uno, qué está mal antes de mandar, qué
 * viaja en el cuerpo y cómo se lee el rechazo del backend. El componente
 * cablea; acá se decide, y acá se prueba.
 *
 * El vocabulario —las claves, los idiomas, los topes y qué formato acepta el
 * documento— NO se copia: sale de `@bandido/shared/types`, que es lo mismo que
 * usa el borde HTTP para validar. Con dos definiciones, el contador mostraba un
 * número y el `PUT` rechazaba en otro.
 */

/** Un bloque tal como viaja en el cuerpo: la forma de `VoucherTextBlockDto`. */
export interface VoucherTextBlockBody {
  scope: VoucherTextScope;
  key: string;
  lang: VoucherTextLanguage;
  body: string;
  /**
   * Solo en un proveedor, y SIEMPRE en un proveedor: nombrar un bloque reescribe
   * su valor entero, así que uno que viajara sin modo volvería a `append`.
   */
  mode?: VoucherTextMode;
}

/** Lo que la pantalla lee del `GET`, sin depender de la clase del DTO. */
export interface VoucherTextBlockRead {
  scope: string;
  key: string;
  lang: string;
  body: string;
  /** Solo significa algo en un conector: `append` (default) o `replace`. */
  mode?: string;
}

/**
 * EL BORRADOR de todos los textos que esta pantalla edita, por identidad de
 * caja. Es un mapa plano y no un árbol por alcance porque todo lo que se hace
 * con él —qué cambió, qué viaja, qué está mal, qué se sincroniza— es recorrer
 * las cajas: con un árbol, cada alcance nuevo duplicaba esos cuatro recorridos.
 */
export type VoucherTextsDraft = Record<string, string>;

/** Una caja del formulario: un bloque en un idioma. */
export interface VoucherTextBox {
  scope: VoucherTextScope;
  key: string;
  lang: VoucherTextLanguage;
  id: string;
}

/**
 * La identidad de una caja, para rotular su contador y sus errores. Lleva el
 * ALCANCE además de la clave porque la clave sola no identifica un bloque: la
 * base lo guarda por `(alcance, clave, idioma)`, y nada obliga a que dos
 * alcances usen claves disjuntas.
 */
export function voucherTextId(
  scope: VoucherTextScope,
  key: string,
  lang: VoucherTextLanguage,
): string {
  return `${scope}:${key}:${lang}`;
}

/**
 * El tope del bloque, sacado de lo que PUBLICA el `GET`: el mismo número con
 * el que rechaza el backend (§21.22). La constante compartida queda de red por
 * si la respuesta no lo trae, para no contar contra `undefined`.
 *
 * El tope se busca por ALCANCE y no por clave: el `GET` publica cuatro
 * (`intro`, `footer`, `vertical`, `connector`), así que buscar `limits['ferry']`
 * no encuentra nada y el contador mostraría el tope de la bienvenida.
 */
export function voucherTextLimit(
  limits: Record<string, number> | undefined,
  scope: VoucherTextScope,
  key: string,
): number {
  const publicado = limits?.[scope === 'agency' ? key : scope];
  if (typeof publicado === 'number' && publicado > 0) return publicado;
  // La red es la MISMA función que mide el borde HTTP, no una copia de la
  // tabla: con dos definiciones, el contador y el rechazo se separan.
  return voucherTextMaxLength(scope, key);
}

/**
 * Se cuenta lo que SE VA A MANDAR: el cuerpo se normaliza al guardar, así que
 * contando el texto crudo un bloque al tope con un salto de línea al final se
 * marcaba pasado y apagaba el Guardar, cuando lo que viajaba entraba perfecto.
 */
export function voucherTextCount(body: string): number {
  return normalizeVoucherText(body).length;
}

/** El estado guardado, al borrador. Lo ausente es cadena vacía: un `undefined`
 * en un `textarea` lo vuelve no controlado y React pierde lo escrito. */
export function voucherTextsDraft(blocks: VoucherTextBlockRead[] | undefined): VoucherTextsDraft {
  const draft: VoucherTextsDraft = {};
  // Los proveedores con algo guardado ya tienen caja; los demás la tienen en
  // cuanto se escribe en ellos (`voucherTextBoxes`).
  for (const block of blocks ?? []) {
    if (block.scope === 'connector') draft[voucherTextModeId(block.key, VOUCHER_TEXT_BASE_LANGUAGE)] = 'append';
  }
  for (const box of voucherTextBoxes(draft)) {
    draft[box.id] =
      blocks?.find(
        (block) => block.scope === box.scope && block.key === box.key && block.lang === box.lang,
      )?.body ?? '';
  }
  for (const key of voucherConnectorKeys(draft)) {
    const modo = (lang: string) =>
      blocks?.find((block) => block.scope === 'connector' && block.key === key && block.lang === lang)?.mode;
    for (const lang of VOUCHER_TEXT_LANGUAGES) {
      // El modo es de CADA bloque, como lo guarda la base y como lo lee la
      // impresión: reducirlo a uno escondía un proveedor que imprime distinto
      // en cada idioma. El idioma sin bloque hereda el del otro, para que
      // escribirlo después salga igual.
      const guardado = modo(lang) ?? VOUCHER_TEXT_LANGUAGES.map(modo).find(Boolean);
      draft[voucherTextModeId(key, lang)] = guardado === 'replace' ? 'replace' : 'append';
    }
  }
  return draft;
}

/**
 * Dónde guarda el borrador el MODO de un idioma de un proveedor. Vive en el
 * mismo mapa que los textos para que "qué cambió" y "qué llegó del servidor" lo
 * vean sin un segundo estado que sincronizar; no es una caja, y
 * `voucherTextBoxes` no lo cuenta como tal.
 */
export function voucherTextModeId(key: string, lang: VoucherTextLanguage): string {
  return `connector:${key}:${lang}:mode`;
}

/** El modo de un idioma de un proveedor: agrega, salvo que diga lo contrario. */
function voucherConnectorLangMode(draft: VoucherTextsDraft, key: string, lang: VoucherTextLanguage): VoucherTextMode {
  return draft[voucherTextModeId(key, lang)] === 'replace' ? 'replace' : 'append';
}

/**
 * El modo que MUESTRA la fila: el de sus idiomas con texto. Si no coinciden es
 * `mixed` —el `PUT` guarda el modo por bloque y nada impide que difieran—, y
 * la fila no puede decir uno solo sin mentir sobre el otro idioma. Sin texto,
 * el del idioma base.
 */
export function voucherConnectorMode(draft: VoucherTextsDraft, key: string): VoucherTextMode | 'mixed' {
  const conTexto = voucherTextBoxesOf('connector', key)
    .filter((box) => normalizeVoucherText(draft[box.id] ?? ''))
    .map((box) => box.lang);
  const [primero, ...resto] = (conTexto.length > 0 ? conTexto : [VOUCHER_TEXT_BASE_LANGUAGE]).map((lang) =>
    voucherConnectorLangMode(draft, key, lang),);
  return resto.some((modo) => modo !== primero) ? 'mixed' : primero;
}

/** Elegir el modo de un proveedor lo fija en TODOS sus idiomas. */
export function voucherConnectorModePatch(key: string, mode: VoucherTextMode): VoucherTextsDraft {
  return Object.fromEntries(VOUCHER_TEXT_LANGUAGES.map((lang) => [voucherTextModeId(key, lang), mode]));
}

/** Los proveedores que tienen caja en alguno de los borradores, sin repetir. */
function voucherConnectorKeys(...drafts: VoucherTextsDraft[]): string[] {
  const keys = new Set<string>();
  for (const draft of drafts) {
    for (const id of Object.keys(draft)) {
      const [scope, key] = id.split(':');
      if (scope === 'connector' && key) keys.add(key);
    }
  }
  return [...keys];
}

/** ¿El proveedor tiene texto en algún idioma? Sin texto, el modo no se guarda. */
function voucherConnectorHasText(draft: VoucherTextsDraft, key: string): boolean {
  return voucherTextBoxesOf('connector', key).some((box) => normalizeVoucherText(draft[box.id] ?? ''));
}

/**
 * ¿Cambió el modo de ESTA caja de proveedor, que tiene texto? Sin texto no hay
 * bloque que lo lleve: contarlo dejaría la pantalla "con cambios" que ningún
 * `PUT` guarda.
 */
function voucherTextModeChanged(
  box: VoucherTextBox,
  body: string,
  draft: VoucherTextsDraft,
  base: VoucherTextsDraft,
): boolean {
  return (
    box.scope === 'connector' &&
    Boolean(body) &&
    voucherConnectorLangMode(draft, box.key, box.lang) !== voucherConnectorLangMode(base, box.key, box.lang)
  );
}

/** El bloque de una caja como viaja: el de un proveedor, con el modo de su idioma. */
function voucherTextBlock(box: VoucherTextBox, body: string, draft: VoucherTextsDraft): VoucherTextBlockBody {
  const block: VoucherTextBlockBody = { scope: box.scope, key: box.key, lang: box.lang, body };
  return box.scope === 'connector' ? { ...block, mode: voucherConnectorLangMode(draft, box.key, box.lang) } : block;
}

/**
 * TODAS LAS CAJAS del formulario, en el orden en que se muestran: los dos
 * textos de la agencia, uno por tipo de producto y uno por cada proveedor que
 * aparece en los borradores que se le pasan, cada uno en los dos idiomas.
 *
 * Los proveedores no son una lista fija —salen del hub, por agencia—: sus cajas
 * son las que el borrador ya tiene, lo guardado o lo que se escribió. Por eso
 * lo que compara dos borradores recorre las cajas de LOS DOS: un proveedor
 * vaciado en uno sigue siendo un cambio respecto del otro.
 *
 * Recorrer esta lista —y no un bucle propio en cada función— es lo que
 * garantiza que el contador, la validación, el cuerpo del guardado y el de la
 * vista previa hablen exactamente de las mismas cajas. Es también el único
 * lugar que hay que tocar para sumar un alcance.
 */
export function voucherTextBoxes(...drafts: VoucherTextsDraft[]): VoucherTextBox[] {
  const cajas: VoucherTextBox[] = [];
  for (const key of VOUCHER_AGENCY_TEXT_KEYS) cajas.push(...voucherTextBoxesOf('agency', key));
  // Los nueve, siempre: un producto sin texto propio usa el de la agencia, y
  // eso es justamente lo que la fila tiene que poder decir.
  for (const key of RUTERO_VERTICALS) cajas.push(...voucherTextBoxesOf('vertical', key));
  for (const key of voucherConnectorKeys(...drafts)) cajas.push(...voucherTextBoxesOf('connector', key));
  return cajas;
}

/** Las cajas de un bloque (sus dos idiomas), para dibujar una fila. */
export function voucherTextBoxesOf(scope: VoucherTextScope, key: string): VoucherTextBox[] {
  return VOUCHER_TEXT_LANGUAGES.map((lang) => ({ scope, key, lang, id: voucherTextId(scope, key, lang) }));
}

/** ¿Hay algo sin guardar? Lo que decide el aviso al navegar y el botón. */
export function voucherTextsChanged(draft: VoucherTextsDraft, saved: VoucherTextsDraft): boolean {
  return voucherTextBoxes(draft, saved).some((box) => {
    const body = normalizeVoucherText(draft[box.id] ?? '');
    return body !== normalizeVoucherText(saved[box.id] ?? '') || voucherTextModeChanged(box, body, draft, saved);
  });
}

/**
 * Qué está mal, por caja, con la MISMA función que valida el borde HTTP
 * (`voucherTextIssues` de shared). Un bloque vacío nunca está mal: vaciarlo es
 * cómo se lo quita del documento, y el backend tampoco lo valida.
 */
export function voucherTextsIssues(
  draft: VoucherTextsDraft,
  limits: Record<string, number> | undefined,
): Record<string, VoucherTextIssue[]> {
  const issues: Record<string, VoucherTextIssue[]> = {};
  for (const box of voucherTextBoxes(draft)) {
    const body = normalizeVoucherText(draft[box.id] ?? '');
    if (!body) continue;
    const found = voucherTextIssues(body, voucherTextLimit(limits, box.scope, box.key));
    if (found.length > 0) issues[box.id] = found;
  }
  return issues;
}

/**
 * LOS BLOQUES que viajan: solo los que ESTE asesor cambió respecto de lo que
 * cargó, nunca los cuatro siempre.
 *
 * El `PUT` toca únicamente los bloques que el cuerpo nombra, así que mandar
 * los cuatro le devolvería a otro administrador —que cambió el pie mientras
 * este escribía la bienvenida— el texto que había leído al abrir la pantalla.
 * Es la misma regla que ya rige para la identidad.
 *
 * Devuelve `undefined` cuando no cambió ninguno: sin la clave `blocks`, el
 * guardado no toca ningún texto.
 */
export function voucherTextsPayload(
  draft: VoucherTextsDraft,
  base: VoucherTextsDraft,
): VoucherTextBlockBody[] | undefined {
  const blocks: VoucherTextBlockBody[] = [];
  for (const box of voucherTextBoxes(draft, base)) {
    const body = normalizeVoucherText(draft[box.id] ?? '');
    // Vaciar un bloque ES un cambio, y viaja con el cuerpo vacío: así se quita
    // del documento, sin un verbo aparte.
    const cambio = body !== normalizeVoucherText(base[box.id] ?? '');
    // Cambiar el modo de un idioma de proveedor reescribe su bloque aunque el
    // cuerpo sea el mismo: el modo vive en cada bloque, y el que no viajara se
    // quedaría imprimiendo con el anterior.
    const porModo = voucherTextModeChanged(box, body, draft, base);
    if (!cambio && !porModo) continue;
    blocks.push(voucherTextBlock(box, body, draft));
  }
  return blocks.length > 0 ? blocks : undefined;
}

/**
 * TODOS los bloques con texto del borrador, no solo los cambiados. Es lo que
 * pide la VISTA PREVIA, que dibuja con lo que recibe y con nada más: mandarle
 * solo lo cambiado sería un documento al que le faltan los textos que sí están
 * guardados. El guardado usa `voucherTextsPayload`, que es lo contrario y por
 * el motivo contrario.
 */
export function voucherTextsAll(draft: VoucherTextsDraft): VoucherTextBlockBody[] {
  const blocks: VoucherTextBlockBody[] = [];
  for (const box of voucherTextBoxes(draft)) {
    const body = normalizeVoucherText(draft[box.id] ?? '');
    // Un bloque vacío no se manda: en el documento no existe.
    if (body) blocks.push(voucherTextBlock(box, body, draft));
  }
  return blocks;
}

/**
 * QUÉ HACER cuando llega configuración nueva del servidor, con la misma regla
 * que la identidad: un borrador que nadie tocó adopta lo nuevo —si no, el
 * cambio de otro administrador aparecía como "cambios sin guardar" y el
 * siguiente Guardar se lo revertía—, y uno tocado no se pisa.
 */
export function voucherTextsSync(params: {
  draft: VoucherTextsDraft;
  base: VoucherTextsDraft;
  incoming: VoucherTextsDraft;
}): { draft: VoucherTextsDraft; base: VoucherTextsDraft } | null {
  if (voucherTextsChanged(params.draft, params.base)) return null;
  if (!voucherTextsChanged(params.incoming, params.base)) return null;
  return { draft: params.incoming, base: params.incoming };
}

/** Una entrada del rechazo, tal como la manda el backend. */
interface VoucherTextBlockIssueRead {
  scope?: unknown;
  key?: unknown;
  lang?: unknown;
  issues?: unknown;
}

/**
 * EL RECHAZO del `PUT`, leído por bloque. El backend devuelve
 * `{ blocks: [{ scope, key, lang, issues }] }` en claves estables, y cada una
 * tiene que aterrizar en la caja que la causó: un cartel general obliga al
 * asesor a adivinar cuál de los cuatro textos tiene el problema.
 *
 * `{ unknownKeys }` es el otro 400: un proveedor que el hub ya no da por
 * configurado para la agencia. Va a SU fila (`sinConfigurar`), no al cartel.
 *
 * Lo que no se puede ubicar en una caja —otro alcance, una clave que esta
 * pantalla no dibuja, o un 400 con otra forma— prende `otros`, para que se
 * muestre el cartel general en vez de tragarse el error.
 *
 * Las cajas de proveedor se buscan en el BORRADOR que se mandó: no son una
 * lista fija.
 */
export function voucherTextsServerIssues(error: unknown, draft: VoucherTextsDraft = {}): VoucherTextRejection {
  const data = (error as { response?: { data?: { blocks?: unknown; unknownKeys?: unknown } } } | null)
    ?.response?.data;
  const cajas = voucherTextBoxes(draft);
  const sinConfigurar: string[] = [];
  let otros = false;
  if (Array.isArray(data?.unknownKeys)) {
    for (const entrada of data.unknownKeys as VoucherTextBlockIssueRead[]) {
      const deProveedor = cajas.some((box) => box.scope === 'connector' && box.key === entrada?.key);
      if (entrada?.scope === 'connector' && deProveedor) {
        if (!sinConfigurar.includes(entrada.key as string)) sinConfigurar.push(entrada.key as string);
      } else {
        otros = true;
      }
    }
  }
  if (!Array.isArray(data?.blocks)) return { blocks: {}, otros, sinConfigurar };

  const blocks: Record<string, VoucherTextIssue[]> = {};
  for (const entrada of data.blocks as VoucherTextBlockIssueRead[]) {
    // La caja se busca por alcance, clave e idioma: la lista de cajas es la
    // misma que dibuja el formulario, así que lo que no está en ella es
    // literalmente algo que esta pantalla no muestra.
    const caja = cajas.find(
      (box) =>
        box.scope === entrada?.scope && box.key === entrada?.key && box.lang === entrada?.lang,
    );
    // Un rechazo sin problemas que mostrar tampoco tiene qué marcar en la
    // fila: la caja se vería limpia mientras el mensaje dice "revisá los
    // textos marcados". Ese también va al cartel general.
    const problemas = Array.isArray(entrada.issues) ? (entrada.issues as VoucherTextIssue[]) : [];
    if (!caja || problemas.length === 0) {
      otros = true;
      continue;
    }
    blocks[caja.id] = problemas;
  }
  return { blocks, otros, sinConfigurar };
}

/**
 * LOS IDIOMAS de un bloque que tienen algo mal. La pantalla dibuja una sola
 * caja —la del idioma elegido—, así que un problema en el otro idioma apagaba
 * el Guardar con un "revisá los campos marcados" y NADA marcado en pantalla:
 * el único camino era ir tocando pestañas hasta encontrarlo. La pestaña que no
 * se está viendo tiene que poder decir que el problema está adentro.
 */
export function voucherTextLangsWithIssues(
  scope: VoucherTextScope,
  key: string,
  issues: Record<string, VoucherTextIssue[]>,
): VoucherTextLanguage[] {
  return voucherTextBoxesOf(scope, key)
    .filter((box) => (issues[box.id] ?? []).length > 0)
    .map((box) => box.lang);
}

/**
 * EL ANCLA donde está el problema que apaga el Guardar, cuando no es la que se
 * está viendo. Mismo motivo: la pantalla muestra un ancla por vez, así que un
 * mail inválido en Identidad apagaba el botón mientras el asesor miraba
 * Textos, sin nada que señalara dónde estaba.
 *
 * Devuelve `null` cuando el problema —si lo hay— se ve desde acá.
 */
export function voucherIssuesElsewhere(context: {
  anchor: string;
  identityIssues: Record<string, unknown>;
  textIssues: Record<string, unknown>;
}): VoucherAnchor | null {
  if (Object.keys(context.identityIssues).length > 0 && context.anchor !== 'identity') {
    return 'identity';
  }
  // El ancla la decide el ALCANCE de la caja, no "hay problemas de texto": con
  // los textos por producto en el mismo mapa, un problema en un producto
  // apagaba el Guardar desde Textos —sin nada marcado ahí— y mandaba al asesor
  // a Textos cuando la fila roja estaba en la otra ancla.
  for (const id of Object.keys(context.textIssues)) {
    const ancla = voucherAnchorOfIssue(id);
    if (ancla && ancla !== context.anchor) return ancla;
  }
  return null;
}

/**
 * EL ANCLA donde está un texto que el BACKEND rechazó, cuando no es la que se
 * está viendo. Guardar es uno solo para todas las anclas: un proveedor que el
 * hub rechaza, guardando desde Textos, dejaba un "revisá los textos marcados"
 * sin nada marcado ahí. No usa el aviso de `voucherIssuesElsewhere` porque un
 * rechazo del servidor no apaga Guardar.
 */
export function voucherRejectionElsewhere(anchor: string, rejection: VoucherTextRejection): VoucherAnchor | null {
  const anclas: (VoucherAnchor | null)[] = [
    ...Object.keys(rejection.blocks).map(voucherAnchorOfIssue),
    ...(rejection.sinConfigurar.length > 0 ? ['byProvider' as const] : []),
  ];
  return anclas.find((ancla) => ancla && ancla !== anchor) ?? null;
}

/** Las anclas de la sección que tienen formulario. */
export type VoucherAnchor = 'identity' | 'texts' | 'byVertical' | 'byProvider';

/** En qué ancla se ve la caja que causó un problema. */
export function voucherAnchorOfIssue(id: string): VoucherAnchor | null {
  const [scope] = id.split(':');
  if (scope === 'agency') return 'texts';
  if (scope === 'vertical') return 'byVertical';
  if (scope === 'connector') return 'byProvider';
  return null;
}

/** Lo que muestra la fila de un tipo de producto sin abrirla. */
export interface VoucherVerticalRow {
  key: string;
  /** Tiene texto propio en ALGÚN idioma. */
  own: boolean;
  /** La primera línea de lo que hay escrito, para reconocerlo sin abrir. */
  preview: string;
  /** Alguna de sus cajas tiene un problema: la fila lo dice cerrada. */
  hasIssues: boolean;
}

/**
 * LAS FILAS de "por tipo de producto": los nueve, siempre, con o sin texto.
 *
 * Los nueve y no solo los que tienen texto porque la lista es también la
 * respuesta a "¿qué pasa con los demás?": el que no tiene propio usa el de la
 * agencia, y eso hay que poder verlo sin abrir nada.
 */
export function voucherVerticalRows(
  draft: VoucherTextsDraft,
  issues: Record<string, VoucherTextIssue[]>,
): VoucherVerticalRow[] {
  return RUTERO_VERTICALS.map((key) => ({ key, ...voucherBlockSummary('vertical', key, draft, issues) }));
}

/** Lo que dice una fila cerrada de un bloque, de cualquier alcance. */
function voucherBlockSummary(
  scope: VoucherTextScope,
  key: string,
  draft: VoucherTextsDraft,
  issues: Record<string, VoucherTextIssue[]>,
): { own: boolean; preview: string; hasIssues: boolean } {
  const cajas = voucherTextBoxesOf(scope, key);
  const cuerpos = cajas.map((box) => normalizeVoucherText(draft[box.id] ?? ''));
  // El resumen sale del idioma BASE —el que shared declara, no el primero de
  // la lista—: un bloque sin inglés se imprime en español, así que es lo que
  // representa la fila. Si solo hay inglés, se muestra ese.
  const enBase = normalizeVoucherText(draft[voucherTextId(scope, key, VOUCHER_TEXT_BASE_LANGUAGE)] ?? '');
  const base = enBase || cuerpos.find((cuerpo) => cuerpo) || '';
  return {
    own: cuerpos.some((cuerpo) => cuerpo.length > 0),
    preview: base.split('\n')[0] ?? '',
    hasIssues: cajas.some((box) => (issues[box.id] ?? []).length > 0),
  };
}

/** Un conector tal como lo publica el hub para la agencia. */
export interface VoucherConnectorRead {
  connector: string;
  provider: string;
  vertical: string;
  configured: boolean;
}

/** Lo que muestra la fila de un proveedor sin abrirla. */
export interface VoucherProviderRow {
  /** La clave del texto: proveedor + tipo de producto (`voucherConnectorTextKey`). */
  key: string;
  connector: string;
  provider: string;
  vertical: string;
  configured: boolean;
  /**
   * Se puede escribir: está configurado, o tiene texto —guardado o escrito—
   * que hay que poder vaciar aunque el hub ya no lo dé por configurado.
   */
  editable: boolean;
  own: boolean;
  preview: string;
  /** `mixed` cuando sus idiomas con texto se imprimen con modos distintos. */
  mode: VoucherTextMode | 'mixed';
  hasIssues: boolean;
  /** El `PUT` dijo que el hub no lo tiene configurado para la agencia. */
  rejectedAsUnknown: boolean;
  /**
   * Se puede elegir el modo: tiene texto —sin texto no hay bloque que lo
   * lleve— y el hub lo tiene configurado —si no, solo se puede vaciar—.
   */
  modeSelectable: boolean;
  /** La ayuda debajo del modo, que explica por qué no se puede elegir cuando no se puede. */
  modeHelpKey: string;
}

/**
 * LAS FILAS de "por proveedor": una por CONECTOR del catálogo del hub, con o
 * sin configurar —los no configurados se ven apagados, nunca se esconden—, y
 * los configurados primero, que son los que la agencia usa.
 *
 * Al final, apagada, una por cada texto de proveedor que el catálogo no lista
 * —un conector que el hub dejó de publicar, o una clave vieja que la migración
 * no pudo traducir—: no coincide con ninguna reserva, pero sin fila no se
 * podría vaciar.
 *
 * Sin catálogo —cargando, o el hub no contestó— NO hay filas: "configurado" es
 * un dato del hub y nada más. Con una lista vacía, cada texto guardado salía
 * como "no configurado, solo se puede vaciar", invitando a borrar textos de
 * proveedores que la agencia sí usa.
 */
export function voucherProviderRows(params: {
  /** `undefined` mientras el catálogo no llegó. */
  connectors: VoucherConnectorRead[] | undefined;
  draft: VoucherTextsDraft;
  saved: VoucherTextsDraft;
  issues: Record<string, VoucherTextIssue[]>;
  sinConfigurar: string[];
}): VoucherProviderRow[] {
  if (!params.connectors) return [];
  const fila = (connector: VoucherConnectorRead, key: string): VoucherProviderRow => {
    const resumen = voucherBlockSummary('connector', key, params.draft, params.issues);
    const rechazado = params.sinConfigurar.includes(key);
    const mode = voucherConnectorMode(params.draft, key);
    return {
      key,
      connector: connector.connector,
      provider: connector.provider,
      vertical: connector.vertical,
      configured: connector.configured,
      editable:
        connector.configured ||
        voucherConnectorHasText(params.draft, key) ||
        voucherConnectorHasText(params.saved, key),
      ...resumen,
      hasIssues: resumen.hasIssues || rechazado,
      mode,
      rejectedAsUnknown: rechazado,
      modeSelectable: connector.configured && resumen.own,
      modeHelpKey: !connector.configured
        ? `${KEY}.byProvider.notConfiguredHelp`
        : resumen.own
          ? `${KEY}.byProvider.${mode}Help`
          : `${KEY}.byProvider.modeNeedsText`,
    };
  };
  const filas = params.connectors.map((connector) =>
    fila(connector, voucherConnectorTextKey(connector.provider, connector.vertical)),);
  const enCatalogo = new Set(filas.map((row) => row.key));
  const huerfanas = voucherConnectorKeys(params.draft, params.saved)
    .filter((key) => !enCatalogo.has(key))
    .filter((key) => voucherConnectorHasText(params.draft, key) || voucherConnectorHasText(params.saved, key))
    .map((key) => {
      const [provider, vertical = ''] = key.split('/');
      return fila({ connector: key, provider, vertical, configured: false }, key);
    });
  return [...filas.filter((row) => row.configured), ...filas.filter((row) => !row.configured), ...huerfanas];
}

/** Un conjunto de bloques rechazados, con lo que no cayó en ninguna fila. */
export interface VoucherTextRejection {
  blocks: Record<string, VoucherTextIssue[]>;
  otros: boolean;
  /** Los proveedores que el hub no da por configurados (`unknownKeys`). */
  sinConfigurar: string[];
}

export const VOUCHER_NO_REJECTION: VoucherTextRejection = { blocks: {}, otros: false, sinConfigurar: [] };

/** ¿No hay nada rechazado? */
export function voucherRejectionEmpty(rejection: VoucherTextRejection): boolean {
  return Object.keys(rejection.blocks).length === 0 && !rejection.otros && rejection.sinConfigurar.length === 0;
}

/**
 * EL AVISO de un guardado que falló. El `PUT` es una transacción: no quedó
 * nada guardado, y el mensaje dice por qué. Un 503 es el hub que no contestó
 * al verificar los proveedores: reintentar sirve, revisar los textos no.
 */
export function voucherSaveFailureKey(error: unknown, rejection: VoucherTextRejection): string {
  if (!voucherRejectionEmpty(rejection)) return `${KEY}.texts.rejected`;
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 503 ? `${KEY}.byProvider.hubUnavailable` : `${KEY}.save.failed`;
}

/**
 * LOS RECHAZOS de las TRES fuentes, en un solo mapa para dibujar.
 *
 * Son tres y no una porque quien los produce es distinto: el `PUT` al guardar,
 * la vista previa mientras se escribe, y el validador de la propia pantalla.
 * Mezclarlas en un estado único hacía que un dibujo exitoso de la vista previa
 * borrara el rechazo del guardado —y justo el que importa: el que el validador
 * compartido NO reproduce, que es el que la preview nunca va a repetir—,
 * dejando al asesor sin ninguna marca y sin saber qué texto rechazó el backend.
 *
 * Lo local pisa a lo remoto: si el asesor ya rompió esa caja, el problema que
 * se muestra tiene que ser el de AHORA.
 */
export function voucherTextsRejections(sources: {
  save: VoucherTextRejection;
  preview: VoucherTextRejection;
  local: Record<string, VoucherTextIssue[]>;
}): VoucherTextRejection {
  return {
    blocks: { ...sources.save.blocks, ...sources.preview.blocks, ...sources.local },
    otros: sources.save.otros || sources.preview.otros,
    sinConfigurar: [...new Set([...sources.save.sinConfigurar, ...sources.preview.sinConfigurar])],
  };
}

/**
 * El rechazo SIN una caja: editarla invalida lo que se dijera de ella, venga
 * de donde venga. Si no queda ninguna, tampoco queda el cartel general: era
 * de un pedido que ya no existe.
 */
export function voucherTextsForget(
  rejection: VoucherTextRejection,
  id: string,
): VoucherTextRejection {
  // Escribir en un proveedor también olvida que el hub lo rechazó: es de un
  // pedido que ya no existe, y la lista del hub se relee al recibirlo.
  const [scope, key] = id.split(':');
  const sinConfigurar =
    scope === 'connector' ? rejection.sinConfigurar.filter((rechazado) => rechazado !== key) : rejection.sinConfigurar;
  if (!rejection.blocks[id] && sinConfigurar.length === rejection.sinConfigurar.length) return rejection;
  const blocks = { ...rejection.blocks };
  delete blocks[id];
  return { blocks, otros: rejection.otros, sinConfigurar };
}

/** La clave de traducción de un problema, en el vocabulario de shared. */
export function voucherTextIssueKey(issue: VoucherTextIssue): string {
  return `${KEY}.texts.issues.${issue}`;
}
